import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchGroupsAsync, deleteGroupAsync, clearError } from '../store/slices/groupsSlice';
import { useLevels } from '../hooks/useLevels';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import Tooltip from './Tooltip';
import './Students.css';
import './GroupForm.css';

const Groups: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groups, pagination, loading, error, totals } = useAppSelector((state) => state.groups);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(''); // '' | 'active' | 'finished'
  const levels = useLevels();

  // Memoized load function to prevent unnecessary re-creation
  const loadGroups = useCallback(() => {
    dispatch(fetchGroupsAsync({ 
      page: currentPage, 
      limit: 10, 
      level: levelFilter || undefined,
      status: (statusFilter as 'active' | 'finished') || undefined
    }));
  }, [dispatch, currentPage, levelFilter, statusFilter]);

  // Load groups when dependencies change
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleLevelFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setLevelFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  const handleStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleDeleteGroup = useCallback(async (id: number, name: string | null) => {
    const confirmMessage = name 
      ? `Are you sure you want to delete the group "${name}"?`
      : `Are you sure you want to delete this group?`;
    
    if (window.confirm(confirmMessage)) {
      await dispatch(deleteGroupAsync(id));
      // Refresh the list using the memoized function
      loadGroups();
    }
  }, [dispatch, loadGroups]);

  const formatDate = useCallback((dateInput?: string | Date | null) => {
    return formatDateEuropean(dateInput);
  }, []);

  // Format date as yyyy-mm-dd (no brackets), handling strings like 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SS'
  const toYMD = useCallback((dateInput?: string | Date | null) => {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
      if (dateInput.includes('T')) return dateInput.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) return dateInput;
    }
    const d = new Date(dateInput as Date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const formatSchedules = useCallback((schedules: { dayOfWeek: string; time: string }[]) => {
    return schedules.map((schedule, index) => {
      // Remove seconds from time (e.g., "19:00:00" -> "19:00")
      const timeWithoutSeconds = schedule.time.substring(0, 5);
      return (
        <div key={index} className="table-schedule-item">
          {schedule.dayOfWeek.charAt(0).toUpperCase() + schedule.dayOfWeek.slice(1)} {timeWithoutSeconds}
        </div>
      );
    });
  }, []);

  if (loading && groups.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Groups</h1>
          <p>Manage student groups and class schedules</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => navigate('/groups/create')}
        >
          Create Group
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <div className="filter-bar">
        <label htmlFor="level-filter" className="filter-label">Level</label>
        <select
          id="level-filter"
          value={levelFilter}
          onChange={handleLevelFilterChange}
          className="form-select"
        >
          <option value="">All Levels</option>
          {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <label htmlFor="status-filter" className="filter-label">Status</label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          className="form-select"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="finished">Finished</option>
        </select>
      </div>

      <div className="results-summary">
        <p>
          Showing {groups.length} of {pagination.totalItems} groups
          {levelFilter && ` at ${levelFilter.toUpperCase()} level`}
          {statusFilter && ` (${statusFilter})`}
        </p>
        <p>Active groups (all levels): {totals?.active ?? 0}</p>
      </div>

      <div className="students-table-container groups-table">
        {groups.length === 0 ? (
          <div className="no-students">
            <p>No groups found.</p>
            <button 
              onClick={() => navigate('/groups/create')}
              className="btn-primary"
            >
              Create your first group
            </button>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Schedule</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      {group.hiddenFromLanding && (
                        <Tooltip text="Hidden from Landing">
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '18px',
                              height: '18px',
                              flexShrink: 0,
                              cursor: 'help',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {group.name || "Unnamed Group"}
                    </span>
                    <div className="group-date-range">
                      {`${toYMD(group.startDate)} - ${group.endDate ? toYMD(group.endDate) : '-'}`}
                    </div>
                  </td>
                  <td>
                    {group.level.toUpperCase()}
                  </td>
                  <td>
                    {formatDate(group.startDate)}
                  </td>
                  <td>
                    {group.endDate ? formatDate(group.endDate) : '-'}
                  </td>
                  <td>
                    <div className="schedule-info">
                      {formatSchedules(group.schedules)}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => navigate(`/groups/edit/${group.id}`)}
                        data-tooltip="Edit group"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => window.open(`/p/vocabulary?group=${group.id}`, '_blank')}
                        data-tooltip="See vocabulary"
                      >
                        ↗
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDeleteGroup(group.id, group.name)}
                        data-tooltip="Delete group"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && groups.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Groups; 